import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2,
  Copy,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatPhp } from "@/lib/format";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";

/**
 * PUBLIC (no auth required) mock hosted-checkout page.
 * Fetches /billing/mock-checkout/{id} for details, then calls .../pay or .../fail.
 * After a successful pay, redirects to the transaction's success_url.
 */
export default function MockCheckoutPage() {
  const { checkoutId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [error, setError] = useState("");
  const [chosenMethod, setChosenMethod] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/billing/mock-checkout/${checkoutId}`);
      setSummary(data);
    } catch (e) {
      setError(formatApiError(e, "Checkout not found"));
    } finally {
      setLoading(false);
    }
  }, [checkoutId]);

  useEffect(() => {
    load();
  }, [load]);

  const pay = async (method) => {
    setChosenMethod(method);
    setWorking(method);
    try {
      const { data } = await api.post(
        `/billing/mock-checkout/${checkoutId}/pay`,
        { method }
      );
      // Give the webhook a heartbeat to flip user to Pro before landing.
      setTimeout(() => {
        if (data.redirect_to && data.redirect_to.startsWith("http")) {
          window.location.href = data.redirect_to;
        } else {
          // Mobile deep link — can't open from a browser; show it instead.
          navigate("/billing/success?deep_link=" + encodeURIComponent(data.redirect_to));
        }
      }, 800);
    } catch (e) {
      toast.error(formatApiError(e, "Payment failed"));
      setWorking(null);
      setChosenMethod(null);
    }
  };

  const cancel = async () => {
    setWorking("cancel");
    try {
      const { data } = await api.post(
        `/billing/mock-checkout/${checkoutId}/fail`,
        { reason: "user_cancelled" }
      );
      setTimeout(() => {
        if (data.redirect_to && data.redirect_to.startsWith("http")) {
          window.location.href = data.redirect_to;
        } else {
          navigate("/billing/cancelled?deep_link=" + encodeURIComponent(data.redirect_to));
        }
      }, 500);
    } catch (e) {
      toast.error(formatApiError(e, "Cancel failed"));
      setWorking(null);
    }
  };

  const copyWebhookCurl = () => {
    // Handy for testers — show them the endpoint. Not the signed body.
    navigator.clipboard.writeText(`${API_BASE}/webhooks/paymongo`);
    toast.success("Webhook URL copied.");
  };

  if (loading) {
    return (
      <div className="mb-page flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#1B2B4B]" />
      </div>
    );
  }
  if (error || !summary) {
    return (
      <div className="mb-page flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-4 text-xl font-bold text-gray-900">
            Checkout unavailable
          </h1>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const methods = [
    { key: "gcash", label: "GCash", subtitle: "Send via GCash app" },
    { key: "maya", label: "Maya", subtitle: "Send via Maya app" },
    { key: "qrph", label: "QR Ph", subtitle: "Instapay national QR" },
    { key: "card", label: "Card", subtitle: "Visa · MasterCard · JCB" },
  ];

  return (
    <div className="mb-page min-h-screen" data-testid="mock-checkout-page">
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between px-6 py-4">
          <BrandLogo className="h-7" />
          <div
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-700"
            data-testid="mock-checkout-banner"
          >
            <ShieldCheck className="h-3 w-3" />
            MOCK PAYMENT — no real charge
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Order summary
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              {summary.product_name}
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Monthly subscription. Cancel anytime.
            </p>
            <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Subtotal</span>
                <span className="font-semibold text-gray-900">
                  {formatPhp(summary.amount / 100)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                <span>VAT</span>
                <span className="text-gray-500">Included</span>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
                <span className="text-sm font-semibold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-gray-900" data-testid="mock-checkout-amount">
                  {formatPhp(summary.amount / 100)}
                </span>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50 p-4 text-xs text-gray-500">
              <div className="font-semibold uppercase tracking-wider text-gray-500">
                Signed in as
              </div>
              <div className="mt-1 text-sm font-semibold text-gray-800">
                {summary.broker_full_name}
              </div>
              <div>{summary.broker_email}</div>
              <div className="mt-3 flex items-center gap-2">
                <span className="rounded-md border border-gray-200 bg-white px-2 py-0.5 font-semibold text-gray-500">
                  {summary.platform}
                </span>
                <button
                  type="button"
                  onClick={copyWebhookCurl}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B2B4B] hover:underline"
                  data-testid="copy-webhook-url"
                >
                  <Copy className="h-3 w-3" />
                  Copy webhook URL
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Payment method
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
              Pay with your preferred wallet
            </h2>

            <div className="mt-6 space-y-3">
              {methods.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  disabled={working != null}
                  onClick={() => pay(m.key)}
                  className={`mb-focus-ring flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition ${
                    chosenMethod === m.key
                      ? "border-[#35622F] bg-[#35622F]/5"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                  } disabled:opacity-60`}
                  data-testid={`pay-with-${m.key}`}
                >
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {m.label}
                    </div>
                    <div className="text-xs text-gray-500">{m.subtitle}</div>
                  </div>
                  {working === m.key ? (
                    <Loader2 className="h-4 w-4 animate-spin text-[#35622F]" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-gray-300" />
                  )}
                </button>
              ))}
            </div>

            <div className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={cancel}
                disabled={working != null}
                className="mb-focus-ring h-10 w-full rounded-xl border-gray-200 bg-white text-sm font-semibold text-gray-500 hover:bg-gray-50 disabled:opacity-60"
                data-testid="mock-checkout-cancel"
              >
                {working === "cancel" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelling…
                  </>
                ) : (
                  "Cancel and go back"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
